// Stage 44: WIKI route — sub-routes parsed from the hash. Mounted from
// AuthedApp at view='wiki'. The hash takes the form:
//
//   #/wiki                         → ProjectsList
//   #/wiki/p/<pid>                 → ProjectHome
//   #/wiki/p/<pid>/page/<slug>     → PageReader
//   #/wiki/p/<pid>/search          → SearchView (q in query string)
//   #/wiki/p/<pid>/query           → QueryConsole
//   #/wiki/p/<pid>/log             → LogView
//   #/wiki/p/<pid>/source/<rid>    → SourceView
//
// Each sub-view is its own component; they share styling via the parent
// gallery-main container that AuthedApp wraps us in.

import { useEffect, useState } from 'react';
import { ProjectsList } from './ProjectsList';
import { ProjectHome } from './ProjectHome';
import { PageReader } from './PageReader';
import { SearchView } from './SearchView';
import { QueryConsole } from './QueryConsole';
import { LogView } from './LogView';
import { SourceView } from './SourceView';

interface ParsedRoute {
  view: 'list' | 'project' | 'page' | 'search' | 'query' | 'log' | 'source';
  pid?: string;
  slug?: string;
  rid?: string;
}

function parseHashSubRoute(): ParsedRoute {
  const raw = (typeof window !== 'undefined' ? window.location.hash : '').replace(
    /^#\/?wiki\/?/,
    '',
  );
  if (!raw) return { view: 'list' };
  const parts = raw.split('/').filter(Boolean);
  // After /wiki/: parts[0] should be 'p' for project paths, else fall back.
  if (parts[0] !== 'p') return { view: 'list' };
  const pid = decodeURIComponent(parts[1] ?? '');
  if (!pid) return { view: 'list' };
  if (parts.length === 2) return { view: 'project', pid };
  if (parts[2] === 'page') {
    const slug = parts.slice(3).join('/');
    if (!slug) return { view: 'project', pid };
    return { view: 'page', pid, slug: decodeURIComponent(slug) };
  }
  if (parts[2] === 'search') return { view: 'search', pid };
  if (parts[2] === 'query') return { view: 'query', pid };
  if (parts[2] === 'log') return { view: 'log', pid };
  if (parts[2] === 'source') {
    const rid = parts.slice(3).join('/');
    return { view: 'source', pid, rid: decodeURIComponent(rid) };
  }
  return { view: 'project', pid };
}

export function WikiRoute() {
  const [route, setRoute] = useState<ParsedRoute>(() => parseHashSubRoute());

  useEffect(() => {
    const onChange = () => setRoute(parseHashSubRoute());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  if (route.view === 'list') return <ProjectsList />;
  if (route.view === 'project' && route.pid)
    return <ProjectHome pid={route.pid} />;
  if (route.view === 'page' && route.pid && route.slug)
    return <PageReader pid={route.pid} slug={route.slug} />;
  if (route.view === 'search' && route.pid) return <SearchView pid={route.pid} />;
  if (route.view === 'query' && route.pid) return <QueryConsole pid={route.pid} />;
  if (route.view === 'log' && route.pid) return <LogView pid={route.pid} />;
  if (route.view === 'source' && route.pid && route.rid)
    return <SourceView pid={route.pid} rid={route.rid} />;

  return <ProjectsList />;
}
